import { useAppSelector } from "@/store/store-hooks";
import { IElementPhotogrammetryScan } from "@/types/ielement-extra-types";
import {
  convertToDateString,
  FaroPopover,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
  selectIElementWorldPosition,
} from "@faro-lotv/app-component-toolbox";
import { Box, Stack } from "@mui/material";
import { useState, type JSX } from "react";
import { AppAwareHtml } from "../renderers/app-aware-html";
import { POPPER_MODIFIERS } from "./placeholder-preview";

type PhotogrammetryScanPreviewPopoverProps = {
  /** The photogrammetry scan to show a preview for */
  photogrammetryScan: IElementPhotogrammetryScan;
};

/**
 * @returns A popover to show for photogrammetry scans (e.g. when hovering over waypoints)
 */
export function PhotogrammetryScanPreviewPopover({
  photogrammetryScan,
}: PhotogrammetryScanPreviewPopoverProps): JSX.Element | null {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const position = useAppSelector(
    selectIElementWorldPosition(photogrammetryScan.id),
  );

  return (
    <AppAwareHtml
      position={position}
      style={{
        backdropFilter: "blur(4px) brightness(40%)",
        borderRadius: 6,
      }}
    >
      {/* anchor element where the popper attaches to */}
      <Box component="div" ref={setAnchorEl} />
      {anchorEl && (
        <FaroPopover
          open
          dark
          disablePortal
          placement="bottom"
          anchorEl={anchorEl}
          showCloseButton={false}
          sx={{
            // Remove default padding around content and make it fit the content
            "& .MuiPaper-root": {
              // Remove padding on top, between content and arrow
              "> .MuiStack-root": {
                gap: 0,
              },
              p: 0,
              maxWidth: "fit-content",
            },
          }}
          modifiers={POPPER_MODIFIERS}
        >
          <Box
            component="div"
            sx={{
              minWidth: 310,
              background: grey[950],
              padding: 1.5,
              borderRadius: "4px",
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.25,
              }}
            >
              <FaroText
                className={NO_TRANSLATE_CLASS}
                variant="labelM"
                color={grey[100]}
              >
                {photogrammetryScan.name}
              </FaroText>
              <FaroText
                className={NO_TRANSLATE_CLASS}
                variant="bodyXS"
                color={grey[100]}
              >
                {photogrammetryScan.createdAt
                  ? convertToDateString(photogrammetryScan.createdAt)
                  : undefined}
              </FaroText>
            </Stack>
          </Box>
        </FaroPopover>
      )}
    </AppAwareHtml>
  );
}
